import { Card, CardContent } from "@/components/ui/card";
import { Package, AlertTriangle, TrendingDown, DollarSign } from "lucide-react";

export default function StockOverview({
  stockItems,
  transactions,
  lowStockCount,
  expiringCount,
  onLowStockClick,
  onExpiringClick,
}) {
  const totalItems = stockItems.length;
  const activeItems = stockItems.filter(
    (item) => item.status === "active"
  ).length;
  const totalValue = stockItems.reduce(
    (sum, item) => sum + item.currentStock * (item.unitCost || 0),
    0
  );
  const recentTransactions = transactions.filter((t) => {
    const transDate = new Date(t.createdAt);
    const today = new Date();

    // Get previous Sunday (start of week)
    const prevSunday = new Date(today);
    const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, etc.
    prevSunday.setDate(today.getDate() - dayOfWeek);
    prevSunday.setHours(0, 0, 0, 0);

    // Get next Sunday (end of week)
    const nextSunday = new Date(prevSunday);
    nextSunday.setDate(prevSunday.getDate() + 7);
    nextSunday.setHours(23, 59, 59, 999);

    return transDate >= prevSunday && transDate <= nextSunday;
  }).length;

  const StatCard = ({ title, value, icon: Icon, color, trend, onClick, clickable }) => (
    <Card
      className={`bg-white/60 backdrop-blur-sm border-slate-200/60 shadow-lg hover:shadow-xl transition-all duration-300 ${
        clickable ? "cursor-pointer hover:border-slate-300" : ""
      }`}
      onClick={clickable ? onClick : undefined}
    >
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <p className="text-sm font-medium text-slate-600">{title}</p>
            <p className="text-3xl font-bold text-slate-900">{value}</p>
            {trend && <p className="text-sm text-slate-500">{trend}</p>}
          </div>
          <div
            className={`p-3 rounded-xl bg-gradient-to-br ${color} shadow-lg`}
          >
            <Icon className="w-6 h-6 text-white" />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      <StatCard
        title="Total Items"
        value={totalItems}
        icon={Package}
        color="from-blue-500 to-blue-600"
        trend={`${activeItems} active`}
      />
      <StatCard
        title="Low Stock Alerts"
        value={lowStockCount}
        icon={AlertTriangle}
        color={
          lowStockCount > 0
            ? "from-red-500 to-red-600"
            : "from-green-500 to-green-600"
        }
        trend={lowStockCount > 0 ? "Click to view details" : "All good"}
        clickable={lowStockCount > 0}
        onClick={onLowStockClick}
      />
      <StatCard
        title="Expiring Soon"
        value={expiringCount}
        icon={TrendingDown}
        color={
          expiringCount > 0
            ? "from-orange-500 to-orange-600"
            : "from-green-500 to-green-600"
        }
        trend={expiringCount > 0 ? "Click to view details" : "Next 30 days"}
        clickable={expiringCount > 0}
        onClick={onExpiringClick}
      />
      <StatCard
        title="Inventory Value"
        value={`R${totalValue.toLocaleString()}`}
        icon={DollarSign}
        color="from-purple-500 to-purple-600"
        trend={`${recentTransactions} transactions this week`}
      />
    </div>
  );
}
